import { ExpectedError } from "../expectedError";
import { normalizeTestUrl } from "../normalizeTestUrl";
import { isBlankConnectionTestUrl } from "./connectionTestWouldSkip";
import type { ConnectorDefinition } from "./types";
import { singleHostFromUrlPattern } from "./urlPatternConversion";

export type CertificateProbeTarget =
  | { kind: "https"; origin: string; host: string; authority: string }
  | { kind: "tcp"; host: string; port: number; authority: string }
  | { kind: "missing"; message: string }
  | { kind: "none" };

export const TCP_PROBE_PROTOCOLS: ReadonlySet<string> = new Set([
  "mongodb",
  "redis",
  "ldap",
  "imap",
  "smtp",
  "winrm",
  "db2",
]);

const TLS_ON_BY_DEFAULT: ReadonlySet<string> = new Set([
  "mongodb",
  "imap",
  "winrm",
]);

const MISSING_HTTP_MESSAGE =
  "To fetch the server’s certificate, enter an HTTPS Test URL, or a URL pattern with a single host such as https://fw01\\.corp\\.example/.*, in this connector’s settings.";
const MISSING_TCP_MESSAGE =
  "Enter the host and port to fetch the server’s certificate.";

function tlsOnConnect(protocol: string, tls: string | undefined): boolean {
  if (protocol === "winrm") return true;
  if (tls === "implicit" || tls === "true") return true;
  if (tls === "starttls" || tls === "off" || tls === "false") return false;
  return TLS_ON_BY_DEFAULT.has(protocol);
}

function httpsTestUrl(
  definition: ConnectorDefinition,
  configuration: Record<string, string>
): URL | null {
  const { connectionTest } = definition;
  if (connectionTest.protocol !== "http") return null;
  let rendered;
  try {
    rendered = connectionTest.request(configuration);
  } catch (error) {
    if (error instanceof ExpectedError) return null;
    throw error;
  }
  const raw = typeof rendered === "string" ? rendered : rendered.url;
  if (isBlankConnectionTestUrl(raw) || /\{[^{}]*\}/.test(raw)) return null;
  const normalized = normalizeTestUrl(raw);
  if (!URL.canParse(normalized)) return null;
  const url = new URL(normalized);
  return url.protocol === "https:" ? url : null;
}

function httpsTarget(url: URL): CertificateProbeTarget {
  return {
    kind: "https",
    origin: url.origin,
    host: url.hostname,
    authority: url.host,
  };
}

export function certificateProbeTarget(
  definition: ConnectorDefinition,
  {
    urlPattern,
    configuration,
  }: { urlPattern: string; configuration: Record<string, string> }
): CertificateProbeTarget {
  if (definition.protocol === "http") {
    const testUrl = httpsTestUrl(definition, configuration);
    if (testUrl) return httpsTarget(testUrl);
    const single = singleHostFromUrlPattern(urlPattern);
    if (!single) return { kind: "missing", message: MISSING_HTTP_MESSAGE };
    return httpsTarget(
      new URL(`https://${single.host}${single.port ? `:${single.port}` : ""}`)
    );
  }

  const { protocol } = definition;
  if (!TCP_PROBE_PROTOCOLS.has(protocol)) return { kind: "none" };
  if (protocol === "mongodb" && configuration.scheme === "mongodb+srv") {
    return { kind: "none" };
  }
  if (!tlsOnConnect(protocol, configuration.tls)) return { kind: "none" };
  const host = configuration.host?.trim();
  const port = configuration.port?.trim();
  if (!host || !port) return { kind: "missing", message: MISSING_TCP_MESSAGE };
  const authorityHost =
    host.includes(":") && !host.startsWith("[") ? `[${host}]` : host;
  return {
    kind: "tcp",
    host,
    port: Number(port),
    authority: `${authorityHost}:${port}`,
  };
}
